import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Send, RefreshCw, Loader2, ChevronDown, ChevronUp, Trash2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-section "Notes & AI Chat" panel.
 *
 *   • The user types into ONE textbox. Two actions:
 *       – "Ask AI"  → answers a question about the current section text
 *                     WITHOUT modifying it.
 *       – "Regenerate with notes" → kicks off a fresh generation. The notes
 *                     are merged into the LLM system prompt under a guarded
 *                     "USER FEEDBACK" block so Claude only acts on items
 *                     that are factually correct, on-topic, and verifiable.
 *                     A short "incorporated vs. skipped" reply lands in the
 *                     thread automatically once the new draft is saved.
 *
 *   • The thread persists in MongoDB at country.section_chats[section_name]
 *     so it survives page reloads.
 *
 * Props:
 *   countryId          — string
 *   sectionKey         — e.g. "executive_summary"
 *   sectionTitle       — display name
 *   isGenerating       — bool, parent-controlled (disables Regenerate)
 *   isSectionGenerated — bool, gates the "Ask AI" path
 *   onRegenerate(notes)→ Promise<void>; parent owns the regen workflow so the
 *                        button continues to play nicely with the existing
 *                        per-country queue + 10-regen limit.
 *   darkMode           — optional
 */
export default function SectionChatBox({
  countryId,
  sectionKey,
  sectionTitle,
  isGenerating,
  isSectionGenerated,
  onRegenerate,
  darkMode = false,
}) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState('');
  const [asking, setAsking] = useState(false);
  const [submittingRegen, setSubmittingRegen] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const scrollRef = useRef(null);

  const fetchThread = async () => {
    if (!countryId || !sectionKey) return;
    setLoadingThread(true);
    try {
      const res = await axios.get(`${API}/countries/${countryId}/sections/${sectionKey}/chat`);
      setThread(res.data?.messages || []);
    } catch {
      // Endpoint may 403 / 404 for users who don't own the country; render
      // an empty thread silently.
      setThread([]);
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    if (open) fetchThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, countryId, sectionKey]);

  // Refetch when the parent finishes a regeneration so the "feedback summary"
  // assistant message appears.
  useEffect(() => {
    if (open && !isGenerating) fetchThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread, asking, submittingRegen]);

  const handleAsk = async () => {
    const msg = draft.trim();
    if (!msg) { toast.error('Type a question first'); return; }
    if (!isSectionGenerated) {
      toast.error('Generate this section first, then ask questions about it');
      return;
    }
    setAsking(true);
    // Optimistic render of the user's question.
    const optimistic = { role: 'user', text: msg, timestamp: new Date().toISOString(), meta: { action: 'ask' } };
    setThread(prev => [...prev, optimistic]);
    setDraft('');
    try {
      await axios.post(`${API}/countries/${countryId}/sections/${sectionKey}/chat`, { message: msg });
      await fetchThread();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chat failed');
      // Re-fetch — the user message was persisted on the server even if the
      // LLM call failed afterwards.
      await fetchThread();
    } finally {
      setAsking(false);
    }
  };

  const handleRegenerate = async () => {
    const notes = draft.trim();
    setSubmittingRegen(true);
    try {
      await onRegenerate(notes); // parent persists notes via /generate-section
      setDraft('');
      // Surface the user's note in the thread immediately even if the parent
      // hasn't refetched yet — server already saved it.
      if (notes) {
        setThread(prev => [
          ...prev,
          { role: 'user', text: notes, timestamp: new Date().toISOString(), meta: { action: 'regenerate' } },
        ]);
      }
    } finally {
      setSubmittingRegen(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear the chat history for this section?')) return;
    try {
      await axios.delete(`${API}/countries/${countryId}/sections/${sectionKey}/chat`);
      setThread([]);
      toast.success('Chat cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear');
    }
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl+Enter → ask. Shift+Enter inserts a newline (default textarea).
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleAsk();
    }
  };

  const panelBg = darkMode ? 'bg-[#0b1c30]/60 border-[#1a365d]' : 'bg-[#f8f9ff] border-[#c3c6d7]/40';
  const txtBase = darkMode ? 'text-white/90' : 'text-[#0b1c30]';
  const txtMuted = darkMode ? 'text-white/60' : 'text-[#737686]';
  const inputBg = darkMode ? 'bg-[#0b1c30] border-[#1a365d] text-white placeholder-white/40' : 'bg-white border-[#c3c6d7]/40 text-[#0b1c30] placeholder-[#9ca3af]';

  return (
    <div className={`mt-4 rounded-xl border ${panelBg} overflow-hidden transition-all`} data-testid={`section-chat-${sectionKey}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`section-chat-toggle-${sectionKey}`}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-black/5 transition-all ${txtBase}`}>
        <span className="flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-[#7c3aed]" />
          Notes &amp; AI Chat
          {thread.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#7c3aed]/15 text-[#7c3aed] text-[10px] font-bold normal-case tracking-normal">
              {thread.length}
            </span>
          )}
          <span className={`ml-2 text-[10px] font-medium normal-case tracking-normal ${txtMuted}`}>
            Ask questions or leave notes for the next regeneration
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0">
          {/* Thread */}
          {(thread.length > 0 || loadingThread) && (
            <div ref={scrollRef}
                 className={`mb-3 max-h-[280px] overflow-y-auto rounded-lg p-3 space-y-3 ${darkMode ? 'bg-black/20' : 'bg-white'} border ${darkMode ? 'border-white/5' : 'border-[#c3c6d7]/30'}`}>
              {loadingThread && thread.length === 0 && (
                <div className={`text-xs ${txtMuted} flex items-center gap-2`}>
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading thread…
                </div>
              )}
              {thread.map((m, i) => {
                const isUser = m.role === 'user';
                const isFeedbackSummary = m.meta?.kind === 'feedback_summary';
                return (
                  <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[88%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap ${
                      isUser
                        ? 'bg-[#004ac6] text-white'
                        : isFeedbackSummary
                          ? (darkMode ? 'bg-[#7c3aed]/20 text-purple-100 border border-[#7c3aed]/40' : 'bg-purple-50 text-purple-900 border border-purple-200')
                          : (darkMode ? 'bg-white/10 text-white/90' : 'bg-[#eff4ff] text-[#0b1c30]')
                    }`}>
                      {!isUser && (
                        <div className={`flex items-center gap-1 mb-1 text-[10px] font-bold uppercase tracking-widest ${isFeedbackSummary ? 'text-[#7c3aed]' : 'text-[#004ac6]'}`}>
                          {isFeedbackSummary ? <><Sparkles className="w-3 h-3" /> Regen summary</> : <><Sparkles className="w-3 h-3" /> AI</>}
                        </div>
                      )}
                      {m.text}
                    </div>
                  </div>
                );
              })}
              {(asking || submittingRegen) && (
                <div className="flex justify-start">
                  <div className={`rounded-xl px-3 py-2 text-xs flex items-center gap-2 ${darkMode ? 'bg-white/10 text-white/70' : 'bg-[#eff4ff] text-[#434655]'}`}>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {asking ? 'Thinking…' : 'Queued for regeneration. Summary will appear here when the new draft lands.'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Composer */}
          <div className={`rounded-lg border ${inputBg.includes('border-[#1a365d]') ? 'border-[#1a365d]' : 'border-[#c3c6d7]/40'} ${darkMode ? 'bg-[#0b1c30]' : 'bg-white'}`}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Ask a question about "${sectionTitle}", or leave notes / corrections to apply on the next regeneration. (Cmd/Ctrl+Enter to ask.)`}
              rows={3}
              data-testid={`section-chat-input-${sectionKey}`}
              className={`w-full p-3 text-sm rounded-t-lg outline-none focus:ring-1 focus:ring-[#004ac6] resize-none ${inputBg} border-0`}
            />
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-[#c3c6d7]/30">
              <button
                onClick={handleAsk}
                disabled={asking || submittingRegen || !draft.trim()}
                data-testid={`section-chat-ask-${sectionKey}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#004ac6] text-white hover:bg-[#003a9c] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                {asking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Ask AI
              </button>
              <button
                onClick={handleRegenerate}
                disabled={isGenerating || submittingRegen || asking || !draft.trim()}
                title={!draft.trim() ? 'Type your notes first' : undefined}
                data-testid={`section-chat-regen-${sectionKey}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#7c3aed] text-white hover:bg-[#5b21b6] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                {submittingRegen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Regenerate with notes
              </button>
              <span className={`text-[10px] ${txtMuted} ml-auto`}>
                AI applies only relevant, verifiable suggestions.
              </span>
              {thread.length > 0 && (
                <button
                  onClick={handleClear}
                  data-testid={`section-chat-clear-${sectionKey}`}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${darkMode ? 'text-white/60 hover:bg-white/10' : 'text-[#737686] hover:bg-[#eff4ff]'} transition-all`}>
                  <Trash2 className="w-3 h-3" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
